/**
 * Hand a task to an AI employee: it becomes a team task on the SyncSuite
 * platform with the task's notes, steps, how-to and source playbook as the
 * brief. The person stays the owner; the AI does the work under them.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Bot, CheckCircle2, Send } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button, Select, Textarea, useToast } from "@/components/ui";
import { firstName } from "@/features/command/ui";
import { agentName } from "./lib";
import type { TeamData, WorkTask } from "./types";

export function HandToAi({
  task, team, ownerName, onHanded,
}: { task: WorkTask; team: TeamData | null; ownerName: string | null; onHanded: (slug: string, agentTaskId: string) => void }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState(task.agent_slug && task.agent_slug !== "default" ? task.agent_slug : "");
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const agents = team?.agents ?? [];
  const available = !!team?.ai_available;
  const blocked = !available
    ? "AI employees appear here once this CRM is connected to SyncSuite."
    : agents.length === 0
      ? "No AI employees yet. Make one by chatting with SyncSuite AI, then hand it work here."
      : null;

  if (task.agent_task_id) {
    return (
      <div className="flex items-start gap-2.5 rounded-xl border border-indigo-400/20 bg-indigo-500/10 px-3 py-2.5 text-xs text-indigo-100">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-indigo-300" />
        <div>
          Handed to {agentName(task.agent_slug, team)}{ownerName ? `, working under ${firstName(ownerName)}` : ""}.{" "}
          <Link to="/team" className="font-semibold underline-offset-2 hover:underline">See its progress on Team</Link>
        </div>
      </div>
    );
  }

  const send = async () => {
    if (!slug) return;
    setBusy(true);
    try {
      const r = await api.post<{ ok: boolean; agent_task_id: string }>("/team/assign", {
        agent_slug: slug, title: task.title, brief: brief.trim() || undefined, task_id: task.id,
      });
      toast.success(`Handed to ${agentName(slug, team)}`, "Its progress and any questions show on the Team page.");
      setOpen(false);
      onHanded(slug, r.agent_task_id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't hand that over");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="space-y-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<Bot className="h-3.5 w-3.5" />}
          disabled={!!blocked}
          onClick={() => setOpen(true)}
          className="w-full sm:w-auto"
        >
          Hand to an AI employee
        </Button>
        {blocked && <div className="text-[11px] leading-relaxed text-zinc-500">{blocked}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-2.5 rounded-xl border border-indigo-400/25 bg-indigo-500/5 p-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-indigo-200">
        <Bot className="h-4 w-4" /> Who should do it?
      </div>
      <Select value={slug} onChange={(e) => setSlug(e.target.value)}>
        <option value="">Pick an AI employee</option>
        {agents.map((a) => <option key={a.slug} value={a.slug}>{a.name}{a.role ? `, ${a.role}` : ""}</option>)}
      </Select>
      <Textarea
        value={brief}
        onChange={(e) => setBrief(e.target.value)}
        placeholder="Anything extra it should know (optional). The notes, steps and how-to go with it."
        className="min-h-[4rem]"
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
        <Button type="button" size="sm" loading={busy} disabled={!slug} leftIcon={<Send className="h-3.5 w-3.5" />} onClick={send}>
          Hand it over
        </Button>
      </div>
    </div>
  );
}
